/**
 * Página de lista de resumos pós-reunião (F12 Wave 8).
 *
 * Exibe todos os resumos do tenant com busca FTS opcional via
 * `GET /api/v1/summaries?q=...`. Clique em linha navega para o detalhe
 * da reunião em `/meetings/{meeting_id}`.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../design-system/components/Card';
import { Spinner } from '../../design-system/components/Spinner';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { useSummariesList } from './hooks';
import type { SummaryListItem } from './api';
import { SummarySearchBar } from './SummarySearchBar';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSentiment(score: number | null): string {
  if (score === null) return '—';
  return `${Math.round(score * 100)}%`;
}

interface SummariesTableProps {
  items: SummaryListItem[];
  onRowClick: (row: SummaryListItem) => void;
}

function SummariesTable({ items, onRowClick }: SummariesTableProps) {
  if (items.length === 0) {
    return <EmptyState title="Nenhum resumo encontrado." />;
  }
  return (
    <Card>
      <table className="dash-table" aria-label="Lista de resumos pós-reunião">
        <thead>
          <tr>
            <th scope="col">Reunião</th>
            <th scope="col">Data</th>
            <th scope="col">Sentimento</th>
            <th scope="col">Resumo (preview)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.meeting_id}
              onClick={() => onRowClick(row)}
              className="dash-table__row--clickable"
              tabIndex={0}
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onRowClick(row);
              }}
              aria-label={`Ver reunião ${row.meeting_title ?? 'sem título'}`}
            >
              <td>{row.meeting_title ?? '—'}</td>
              <td>{formatDate(row.created_at)}</td>
              <td>{formatSentiment(row.sentiment_score)}</td>
              <td className="muted">{row.preview}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function SummariesListPage() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const list = useSummariesList(q);

  function handleRowClick(row: SummaryListItem) {
    void navigate(`/meetings/${row.meeting_id}`);
  }

  return (
    <div className="dash-section">
      <header className="dash-section__header">
        <h1 className="dash-section__title">Resumos pós-reunião</h1>
      </header>

      <SummarySearchBar value={q} onChange={setQ} />

      {list.isPending ? (
        <div className="center">
          <Spinner label="Carregando resumos" />
        </div>
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <SummariesTable items={list.data.items} onRowClick={handleRowClick} />
      )}
    </div>
  );
}
